import React, { useRef } from "react";
import { ConvergeCell, MarginCell, ReturnCell } from "@/components/FuturesScenario";
import { Pin, PinOff } from "lucide-react";
import { ECON_TITLE } from "@/lib/labels";
import { inr, num } from "@/lib/format";
import { ExactSymbol, PairCell } from "@/components/OpportunityInfo";
import { resolvePinned } from "@/lib/opportunityPins";

function hhmmss(at) {
  if (!at) return "—";
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString("en-IN", { hour12: false });
}

/** Why Add Trade is unavailable on a pinned row that is not live right now. */
export function staleTradeReason(status) {
  return status === "NOT_IN_FEED"
    ? "This contract pair is not in the live feed right now (expired, rolled, or the market is closed). A trade can only be added from a live opportunity."
    : `Market data is ${status}. Adding a trade re-checks the opportunity on live prices (both legs fresh) and would be refused as OPPORTUNITY_STALE until fresh prices return.`;
}

/** Pinned opportunities for ONE market. Rows stay in pin order — never re-ranked — while their
 *  values refresh from every poll. A row that is not live keeps its last-known values with a
 *  STALE badge and the time they were last fresh; it updates itself when fresh data returns. */
export function PinnedOpportunities({ market, pins, rows, onTrade, onUnpin, testIdPrefix = "pinned" }) {
  // last FRESH row per opportunity id, kept across polls (display only)
  const memory = useRef({});
  const mine = (pins || []).filter((p) => p.market === market);
  if (mine.length === 0) return null;

  const resolved = mine.map((p) => {
    const r = resolvePinned(p, rows, memory.current);
    if (r.live) memory.current[p.opportunity_id] = { row: r.row, at: new Date().toISOString() };
    return { pin: p, ...r };
  });

  return (
    <div className="term-panel rounded-sm overflow-auto" data-testid={`${testIdPrefix}-${market.toLowerCase()}`}>
      <div className="px-2.5 py-1.5 text-[11px] font-semibold text-slate-300 flex items-center gap-1.5 border-b border-[#2A2F3A]">
        <Pin className="h-3.5 w-3.5 text-[#FFCC00]" /> Pinned · {market}
        <span className="font-normal text-slate-500">· fixed order, live values · {mine.length}</span>
      </div>
      <table className="w-full text-xs border-collapse">
        <thead className="bg-[#1A1D24] text-slate-400">
          <tr>{["", "Underlying", "Pair", "SELL (exact)", "BUY (exact)", "Converge P&L", "Return on margin", "Margin",
                "Near LTP", "Far LTP", "Data", "Trade"].map((h, i) => (
            <th key={i} title={ECON_TITLE[h]} className="px-1.5 py-1.5 whitespace-nowrap font-semibold text-left">{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {resolved.map(({ pin, row, status, at, live }) => {
            const id = pin.opportunity_id;
            const name = row.commodity || row.name || id.split(":")[1];
            const stale = !live;
            return (
              <tr key={pin.id} className={`gz-row mono ${stale ? "opacity-70" : ""}`} data-testid={`${testIdPrefix}-row-${id}`}>
                <td className="px-1.5 py-1.5">
                  <button onClick={() => onUnpin(pin.id)} title="Unpin" data-testid={`unpin-${id}`}
                    className="text-[#FFCC00] hover:text-slate-200"><PinOff className="h-3.5 w-3.5" /></button>
                </td>
                <td className="px-1.5 py-1.5 font-bold text-slate-100 whitespace-nowrap">{name}</td>
                <td className="px-1.5 py-1.5"><PairCell row={row} /></td>
                <td className="px-1.5 py-1.5 max-w-[9rem]"><ExactSymbol symbol={row.sell_leg || row.sell_symbol} cls="text-[#FF6b62]" /></td>
                <td className="px-1.5 py-1.5 max-w-[9rem]"><ExactSymbol symbol={row.buy_leg || row.buy_symbol} cls="text-[#5b9bff]" /></td>
                <td className="px-1.5 py-1.5 whitespace-nowrap"><ConvergeCell row={row} /></td>
                <td className="px-1.5 py-1.5 whitespace-nowrap"><ReturnCell row={row} /></td>
                <td className="px-1.5 py-1.5 whitespace-nowrap"><MarginCell row={row} /></td>
                <td className="px-1.5 py-1.5 whitespace-nowrap">{num(row.near_ltp, 2)}</td>
                <td className="px-1.5 py-1.5 whitespace-nowrap">{num(row.far_ltp, 2)}</td>
                <td className="px-1.5 py-1.5 whitespace-nowrap" data-testid={`${testIdPrefix}-status-${id}`}>
                  {stale
                    ? <span className="text-[#FFCC00] font-bold" title={`Last fresh ${hhmmss(at)} — values shown are last known`}>
                        {status === "NOT_IN_FEED" ? "NOT IN FEED" : "STALE"} · {hhmmss(at)}</span>
                    : <span className="text-[#00C805]">LIVE</span>}
                </td>
                <td className="px-1.5 py-1.5">
                  <button disabled={stale} onClick={() => onTrade({ ...row })} data-testid={`${testIdPrefix}-trade-${id}`}
                    title={stale ? staleTradeReason(status) : "Add Trade — opens the configuration drawer"}
                    className="text-[10px] font-bold px-2 py-1 rounded-sm bg-[#2563EB] whitespace-nowrap disabled:opacity-30 disabled:cursor-not-allowed">
                    ADD TRADE</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** The pin toggle shown on every main-table row. */
export function PinToggle({ row, market, pinFor, pin, unpin }) {
  if (!row?.opportunity_id) return null;
  const existing = pinFor(row.opportunity_id);
  return (
    <button onClick={() => (existing ? unpin(existing.id) : pin(market, row)).catch(() => {})}
      title={existing ? "Unpin" : "Pin this contract pair"} data-testid={`pin-${row.opportunity_id}`}
      className={`mr-1 align-middle ${existing ? "text-[#FFCC00]" : "text-slate-600 hover:text-slate-300"}`}>
      <Pin className="h-3.5 w-3.5" />
    </button>
  );
}
